import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  CornerDownLeft,
  FileText,
  MapPin,
  Package,
  Receipt,
  Search,
  ShoppingCart,
  Tag,
  Truck,
  Undo2,
} from 'lucide-react';
import { useBusca } from '@/hooks/consultas';
import { useContexto } from '@/hooks/useContexto';
import { useDebounce } from '@/hooks/useDebounce';
import { cn, numero } from '@/lib/utils';
import type { PedidoEncontrado } from '@/lib/tipos';
import { ImagemProduto } from './produto/ImagemProduto';
import { RaioXNota } from './pedido/RaioXNota';
import { RaioXPedido } from './pedido/RaioXPedido';
import { LinhasEsqueleto } from './ui/primitivos';
import { EstadoErro, EstadoVazio } from './ui/estados';

interface Sugestao {
  chave: string;
  icone: React.ReactNode;
  /** SKU quando a sugestão é um produto — rende miniatura no lugar do ícone. */
  sku?: number | undefined;
  titulo: string;
  auxiliar: string;
  /** Para onde o Enter leva. Vazio quando a linha ABRE DETALHE em vez de ir. */
  destino: string;
  /**
   * O pedido, quando a linha é um pedido.
   *
   * Pedido NÃO navega: abre a gaveta de detalhe dentro da própria busca. Quem
   * digitou a televenda quer saber em que pé está o pedido — status, canal,
   * nota, romaneio — e mandá-lo para outra tela para depois voltar é o
   * caminho mais longo até a mesma resposta. Só o romaneio, lá dentro, leva
   * para fora, porque aí a pessoa vai de fato trabalhar no romaneio.
   */
  pedido?: PedidoComTelevenda | undefined;
  /** A nota sem pedido: abre o raio-X da nota, também aqui dentro. */
  notaId?: number | undefined;
}

type PedidoComTelevenda = PedidoEncontrado & { televenda: number };

const ROTULO_TIPO: Record<string, { texto: string; classe: string }> = {
  sku: { texto: 'SKU', classe: 'bg-primaria-suave text-primaria ring-primaria/20' },
  endereco: { texto: 'endereço', classe: 'bg-status-livreFundo text-status-livre ring-status-livre/25' },
  descricao: { texto: 'descrição', classe: 'bg-status-atencaoFundo text-status-atencao ring-status-atencao/25' },
};

/**
 * Busca única (regra 1): um campo só, sem escolher antes se é SKU, endereço
 * ou descrição — a API detecta. O que muda aqui é só como o resultado é
 * apresentado e para onde ele leva.
 */
export function BuscaUniversal(): JSX.Element {
  const [aberta, definirAberta] = React.useState(false);
  const [termo, definirTermo] = React.useState('');
  const [indice, definirIndice] = React.useState(0);
  /** O pedido aberto na gaveta. `null` = mostrando a lista de resultados. */
  const [detalhe, definirDetalhe] = React.useState<PedidoComTelevenda | null>(null);
  /** A nota sem pedido aberta na gaveta (`NF_FATURAMENTO`). */
  const [nota, definirNota] = React.useState<number | null>(null);
  const aberto = detalhe !== null || nota !== null;
  const { centro } = useContexto();
  const navegar = useNavigate();
  const termoAtrasado = useDebounce(termo, 250);
  const consulta = useBusca(centro, termoAtrasado);

  React.useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent): void => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'k') {
        evento.preventDefault();
        definirAberta(true);
      }
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, []);

  const dados = consulta.data;
  const sugestoes = React.useMemo<Sugestao[]>(() => {
    if (!dados || centro === null) return [];
    const base = `/estoque?centro=${centro}`;

    /*
      PEDIDO E ROMANEIO VÊM PRIMEIRO, e não é ordem arbitrária.

      Quem digita um número de 7 dígitos aqui está com o cliente no telefone ou
      com o papel do romaneio na mão — é busca de atendimento, e ela tem
      resposta exata. O resultado de estoque para o mesmo número é um palpite
      (pode ser SKU, pode ser coincidência) e por isso fica abaixo, sem sumir.
    */
    const deNegocio: Sugestao[] = [];

    for (const p of dados.pedidos ?? []) {
      /* Nota sem pedido (transferência, nota manual): não há raio-X de
         pedido, então abre o da nota. */
      if (p.televenda === null) {
        deNegocio.push({
          chave: `nf-${p.empresa}-${p.nfNumero}-${p.nfSerie ?? ''}`,
          icone: <Receipt className="h-4 w-4" />,
          titulo: `NF ${p.nfNumero}${p.nfSerie ? ` · série ${p.nfSerie}` : ''}`,
          auxiliar: ['sem pedido', p.nfSituacao, p.cliente, `empresa ${p.empresa}`]
            .filter(Boolean)
            .join(' · '),
          destino: '',
          notaId: p.nfId ?? undefined,
        });
        continue;
      }
      const pedido: PedidoComTelevenda = { ...p, televenda: p.televenda };
      const porNota = p.via === 'nf' || p.via === 'nfd';
      deNegocio.push({
        chave: `ped-${p.via}-${p.televenda}-${p.nfNumero ?? ''}-${p.nfSerie ?? ''}`,
        icone:
          p.via === 'nfd' ? (
            <Undo2 className="h-4 w-4" />
          ) : p.via === 'nf' ? (
            <Receipt className="h-4 w-4" />
          ) : p.via === 'mkt' ? (
            <ShoppingCart className="h-4 w-4" />
          ) : (
            <FileText className="h-4 w-4" />
          ),
        titulo:
          p.via === 'nfd'
            ? `NFD ${p.nfNumero}${p.nfSerie ? ` · série ${p.nfSerie}` : ''}`
            : p.via === 'nf'
              ? `NF ${p.nfNumero}${p.nfSerie ? ` · série ${p.nfSerie}` : ''}`
              : p.via === 'mkt'
                ? `Pedido ${p.pedidoMarketplace}`
                : `Televenda ${p.televenda}`,
        auxiliar:
          [
            p.via === 'nfd' ? 'devolução' : null,
            porNota || p.via === 'mkt' ? `televenda ${p.televenda}` : p.estadoDescricao,
            porNota || p.via === 'mkt' ? p.estadoDescricao : p.canal,
            p.cliente,
          ]
            .filter(Boolean)
            .join(' · ') || `empresa ${p.empresa}`,
        destino: '',
        pedido,
      });
    }

    if (dados.romaneio) {
      const r = dados.romaneio;
      deNegocio.push({
        chave: `rom-${r.romaneio}`,
        icone: <Truck className="h-4 w-4" />,
        titulo: `Romaneio ${r.romaneio}`,
        auxiliar: `${numero(r.pedidos)} ${r.pedidos === 1 ? 'pedido' : 'pedidos'} · empresa ${r.empresas.join(', ')}`,
        destino: `/romaneios?centro=${centro}&romaneio=${r.romaneio}`,
      });
    }

    if (dados.tipoDetectado === 'sku') {
      return [
        ...deNegocio,
        {
          chave: `sku-${dados.termo}`,
          icone: <Tag className="h-4 w-4" />,
          titulo: `SKU ${dados.termo}`,
          auxiliar: `${numero(dados.total)} ${dados.total === 1 ? 'posição' : 'posições'}`,
          destino: `${base}&q=${encodeURIComponent(dados.termo)}`,
        },
      ];
    }
    if (dados.tipoDetectado === 'endereco') {
      return [...deNegocio, ...dados.enderecos.slice(0, 30).map((endereco) => ({
        chave: `end-${endereco.localizador}`,
        icone: <MapPin className="h-4 w-4" />,
        titulo: endereco.descricao || `Localizador ${endereco.localizador}`,
        // O código do PBS vai junto: é por ele que se confere com o ERP.
        auxiliar: `endereço · LOC ${endereco.localizador}`,
        // Endereço abre na Lista, onde vive a gaveta de detalhe da posição.
        destino: `${base}&vista=lista&pos=${endereco.localizador}`,
      }))];
    }
    return [...deNegocio, ...dados.produtos.slice(0, 30).map((produto) => ({
      chave: `prod-${produto.produto}`,
      icone: <Package className="h-4 w-4" />,
      sku: produto.produto,
      titulo: produto.descricao || `Produto ${produto.produto}`,
      auxiliar: `SKU ${produto.produto}`,
      destino: `${base}&q=${produto.produto}`,
    }))];
  }, [dados, centro]);

  React.useEffect(() => {
    definirIndice(0);
    /* Termo novo, pergunta nova: a gaveta do pedido anterior nao pode
       sobreviver a busca que a abriu. */
    definirDetalhe(null);
    definirNota(null);
  }, [termoAtrasado]);

  const escolher = (sugestao: Sugestao | undefined): void => {
    if (!sugestao) return;
    /* Pedido abre a gaveta aqui dentro; o resto navega e fecha a busca. */
    if (sugestao.pedido) {
      definirDetalhe(sugestao.pedido);
      return;
    }
    if (sugestao.notaId !== undefined) {
      definirNota(sugestao.notaId);
      return;
    }
    if (!sugestao.destino) return;
    definirAberta(false);
    definirTermo('');
    definirDetalhe(null);
    definirNota(null);
    navegar(sugestao.destino);
  };

  /** Sair da busca por um link do detalhe (hoje, o romaneio). */
  const irPara = (destino: string): void => {
    definirAberta(false);
    definirTermo('');
    definirDetalhe(null);
    definirNota(null);
    navegar(destino);
  };

  const aoTeclarNoCampo = (evento: React.KeyboardEvent<HTMLInputElement>): void => {
    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      definirIndice((i) => Math.min(i + 1, sugestoes.length - 1));
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      definirIndice((i) => Math.max(i - 1, 0));
    } else if (evento.key === 'Enter') {
      evento.preventDefault();
      escolher(sugestoes[indice]);
    }
  };

  /*
    A ETIQUETA SÓ FALA QUANDO O ESTOQUE ACHOU ALGO.

    `tipoDetectado` cai em 'descricao' quando nada de estoque casou — é o ramo
    final da API, não uma conclusão. Com a busca por televenda, o resultado era
    uma tarja laranja escrita DESCRIÇÃO em cima de um pedido, que é o oposto de
    ajudar a ler. Sem resultado de estoque, a etiqueta some e as linhas se
    explicam sozinhas.
  */
  const achouEstoque =
    dados !== undefined &&
    (dados.tipoDetectado === 'sku'
      ? dados.posicoes.length > 0
      : dados.tipoDetectado === 'endereco'
        ? dados.enderecos.length > 0
        : dados.produtos.length > 0);
  const tipo = dados && achouEstoque && !aberto ? ROTULO_TIPO[dados.tipoDetectado] : undefined;

  return (
    <>
      <button
        type="button"
        onClick={() => definirAberta(true)}
        className={cn(
          'group flex min-h-[42px] items-center gap-2.5 rounded-xl border border-borda bg-superficie2 px-3 text-[13px] text-textoFraco shadow-xs transition-all',
          'hover:border-bordaForte hover:bg-superficie hover:shadow-cartao',
          'w-10 justify-center sm:w-64 sm:justify-start lg:w-80',
        )}
      >
        <Search className="h-[18px] w-[18px] shrink-0 transition-colors group-hover:text-primaria" />
        <span className="hidden truncate sm:inline">SKU, endereço, televenda, NF…</span>
        <kbd className="ml-auto hidden shrink-0 items-center gap-0.5 rounded-md border border-borda bg-superficie px-1.5 py-0.5 font-sans text-[10px] font-bold text-textoSuave sm:flex">
          Ctrl K
        </kbd>
      </button>

      <Dialog.Root
        open={aberta}
        onOpenChange={(v) => {
          definirAberta(v);
          if (!v) {
            definirDetalhe(null);
            definirNota(null);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-[3px] data-[state=open]:animate-aparecer" />
          <Dialog.Content
            className={cn(
              'fixed left-1/2 top-[6vh] z-50 -translate-x-1/2 overflow-hidden rounded-2xl border border-borda bg-superficie shadow-forte transition-[width] data-[state=open]:animate-escalar',
              /* O raio-X do pedido precisa de largura: produtos com foto ao
                 lado do histórico de status. */
              detalhe
                ? 'top-[3vh] w-[min(1120px,96vw)]'
                : nota !== null
                  ? 'top-[3vh] w-[min(820px,96vw)]'
                  : 'w-[min(680px,94vw)]',
            )}
          >
            <Dialog.Title className="sr-only">Busca</Dialog.Title>
            <Dialog.Description className="sr-only">
              Digite SKU, descrição, endereço, televenda, nota fiscal ou romaneio. O sistema identifica o tipo sozinho.
            </Dialog.Description>

            <div className="flex items-center gap-3 border-b border-borda px-4">
              <Search className="h-5 w-5 shrink-0 text-primaria" />
              <input
                autoFocus
                value={termo}
                onChange={(e) => definirTermo(e.target.value)}
                onKeyDown={aoTeclarNoCampo}
                placeholder="SKU, descrição, endereço, televenda, NF ou romaneio"
                aria-label="Busca universal"
                className="min-h-[60px] w-full bg-transparent text-[16px] font-medium outline-none placeholder:font-normal placeholder:text-textoSuave"
              />
              {tipo ? (
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide ring-1 ring-inset',
                    tipo.classe,
                  )}
                >
                  {tipo.texto}
                </span>
              ) : null}
            </div>

            <div className={cn('overflow-y-auto p-2', aberto ? 'max-h-[calc(94vh-62px)]' : 'max-h-[52vh]')}>
              {detalhe ? (
                <RaioXPedido
                  pedido={detalhe}
                  aoVoltar={() => definirDetalhe(null)}
                  aoAbrirRomaneio={(r: number) =>
                    irPara(`/romaneios?centro=${centro ?? ''}&romaneio=${r}`)
                  }
                  aoAbrirSeparacao={(lista: number) =>
                    irPara(`/separacao?centro=${centro ?? ''}&aba=etiquetas&emissao=${lista}`)
                  }
                />
              ) : nota !== null ? (
                <RaioXNota id={nota} aoVoltar={() => definirNota(null)} />
              ) : termo.trim().length < 2 ? (
                <EstadoVazio
                  titulo="Digite ao menos 2 caracteres"
                  detalhe="Ex.: 32991 (SKU), PP-012-03-1 (endereço), 6536892 (televenda), 222735 (NF) ou 769464 (romaneio)."
                  icone={<Search className="h-6 w-6" />}
                />
              ) : consulta.isLoading ? (
                <div className="p-4">
                  <LinhasEsqueleto linhas={3} />
                </div>
              ) : consulta.isError ? (
                <EstadoErro erro={consulta.error} />
              ) : sugestoes.length === 0 ? (
                <EstadoVazio
                  titulo="Nada encontrado"
                  detalhe={`Nenhum resultado para "${termo}" neste centro.`}
                />
              ) : (
                <ul role="listbox" aria-label="Resultados" className="space-y-0.5">
                  {sugestoes.map((sugestao, i) => (
                    <li key={sugestao.chave}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={i === indice}
                        onMouseEnter={() => definirIndice(i)}
                        onClick={() => escolher(sugestao)}
                        className={cn(
                          'flex min-h-[52px] w-full items-center gap-3 rounded-xl px-3 text-left transition-colors',
                          i === indice ? 'bg-primaria-suave' : 'hover:bg-superficie2',
                        )}
                      >
                        {sugestao.sku !== undefined ? (
                          <ImagemProduto sku={sugestao.sku} tamanho={36} alt="" />
                        ) : (
                          <span
                            className={cn(
                              'grid h-9 w-9 shrink-0 place-items-center rounded-lg',
                              i === indice
                                ? 'bg-primaria text-white'
                                : 'bg-superficie2 text-textoFraco',
                            )}
                          >
                            {sugestao.icone}
                          </span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13.5px] font-semibold">
                            {sugestao.titulo}
                          </span>
                          <span className="block truncate text-[11.5px] text-textoFraco">
                            {sugestao.auxiliar}
                          </span>
                        </span>
                        {i === indice ? (
                          <CornerDownLeft className="h-4 w-4 shrink-0 text-primaria" />
                        ) : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex items-center gap-4 border-t border-borda bg-superficie2 px-4 py-2 text-[11px] text-textoFraco">
              <span className={cn('flex items-center gap-1.5', aberto && 'hidden')}>
                <Tecla>
                  <ArrowUp className="h-2.5 w-2.5" />
                </Tecla>
                <Tecla>
                  <ArrowDown className="h-2.5 w-2.5" />
                </Tecla>
                navegar
              </span>
              <span className={cn('flex items-center gap-1.5', aberto && 'hidden')}>
                <Tecla>
                  <CornerDownLeft className="h-2.5 w-2.5" />
                </Tecla>
                abrir
              </span>
              <span className="ml-auto flex items-center gap-1.5">
                <Tecla>esc</Tecla>
                fechar
              </span>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}

function Tecla({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <kbd className="inline-flex min-w-[20px] items-center justify-center rounded border border-borda bg-superficie px-1 py-0.5 font-sans text-[10px] font-bold text-textoFraco shadow-xs">
      {children}
    </kbd>
  );
}

